import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { Gender, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { AgeUnit, ChartId, VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { AxisConfiguration } from "@/Patient/Models/GrowthCharts/ChartData";
import { CurvesName } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { GrowthChartModel } from "./GrowthChartModel";

abstract class FentonGrowthChart extends GrowthChartModel {
    public get isPlotSelectionSupported(): boolean {
        return false;
    }

    public get isFentonChart(): boolean {
        return true;
    }

    public isDisplayedByDefault(): boolean {
        if (!this.patient.DateOfBirth)
            return false;

        return LuxonDateHelper.dateDiff(this.patient.DateOfBirth, DateTime.nowDate(), "years") <= 2 && this.getCorrectedAge() < 0;
    }

    public isCompatible(): boolean {
        return super.isCompatible()
            && this.patient.IsBornPremature
            && !!this.patient.BornPrematureWeeks
            && this.vitalSigns
                .filter(vital => !!vital[this.vitalProperty])
                .some(vital => this.filterVitalSign(vital));
    }

    public filterVitalSign(vitalSign: MedicalItemVitalSign): boolean {
        const weeks = LuxonDateHelper.dateDiff(this.patient.DateOfBirth, vitalSign.PharmaStartDate, "weeks") +
            (this.patient.BornPrematureWeeks ?? 0);
        return weeks >= 22 && weeks <= 50;
    }

    public get isVitalSign(): boolean {
        return false;
    }

    public get ageUnit(): AgeUnit {
        return "week";
    }

    public get xAxis(): AxisConfiguration {
        return {
            label: `${cacheStore.GetString("GestationalAgeLabel")} (${cacheStore.GetString("WeekAbbrevLabel").toLowerCase()})`,
            min: 22,
            max: 50,
            interval: 1
        };
    }
}

class FentonWeightForAge extends FentonGrowthChart {
    public get id(): ChartId {
        return "FWFA";
    }

    public get name(): string {
        return cacheStore.GetString("FentonWeightForAgeLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("FentonWeightForAgeShortLabel");
    }

    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "fentonWeightMale" : "fentonWeightFemale";
    }

    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric ?
            {
                label: `${cacheStore.GetString("WeightLabel")} (${cacheStore.GetString("KilogramUnitSymbolLabel")})`,
                min: 0,
                max: 7.5,
                interval: 0.5
            } :
            {
                label: `${cacheStore.GetString("WeightLabel")} (${cacheStore.GetString("PoundUnitSymbolLabel")})`,
                min: 0,
                max: 17,
                interval: 1
            };
    }

    public get vitalProperty(): VitalProperty {
        return "Weight";
    }
}

class FentonLengthForAge extends FentonGrowthChart {
    public get id(): ChartId {
        return "FLFA";
    }

    public get name(): string {
        return cacheStore.GetString("FentonLengthForAgeLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("FentonLengthForAgeShortLabel");
    }

    public get yLabel(): string {
        return cacheStore.GetString("LengthLabel") + ` (${this.unit === UnitSystem.Metric ? cacheStore.GetString("CentimeterUnitSymbolLabel") : cacheStore.GetString("InchUnitSymbolLabel")})`;
    }

    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "fentonLenghtMale" : "fentonLenghtFemale";
    }

    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric ?
            {
                label: `${cacheStore.GetString("LengthLabel")} (${cacheStore.GetString("CentimeterUnitSymbolLabel")})`,
                min: 20,
                max: 65,
                interval: 5
            } :
            {
                label: `${cacheStore.GetString("LengthLabel")} (${cacheStore.GetString("InchUnitSymbolLabel")})`,
                min: 8,
                max: 26,
                interval: 2
            };
    }

    public get vitalProperty(): VitalProperty {
        return "Height";
    }
}

class FentonHeadCircumference extends FentonGrowthChart {
    public get id(): ChartId {
        return "FHCFA";
    }

    public get name(): string {
        return cacheStore.GetString("FentonHeadCircumferenceLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("FentonHeadCircumferenceShortLabel");
    }

    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "fentonHeadCircMale" : "fentonHeadCircFemale";
    }

    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric ?
            {
                label: `${cacheStore.GetString("LengthLabel")} (${cacheStore.GetString("CentimeterUnitSymbolLabel")})`,
                min: 15,
                max: 45,
                interval: 2
            } :
            {
                label: `${cacheStore.GetString("LengthLabel")} (${cacheStore.GetString("InchUnitSymbolLabel")})`,
                min: 6,
                max: 18,
                interval: 1
            };
    }

    public get vitalProperty(): VitalProperty {
        return "HeadCircumference";
    }
}

export { FentonHeadCircumference, FentonLengthForAge, FentonWeightForAge };
